/* @license
 * Copyright 2019 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { CanvasTexture, DoubleSide, LinearFilter, Mesh, MeshBasicMaterial, Object3D, PlaneGeometry, SRGBColorSpace, } from 'three';
/**
 * PDF object that can display PDF pages as textures.
 */
export class PDFObject extends Object3D {
    constructor() {
        super();
        this._src = null;
        this._pdf = null; // PDF.js document
        this._currentPage = 1;
        this._totalPages = 0;
        this._pdfjsLib = null;
        // Create canvas for PDF rendering
        this._canvas = document.createElement('canvas');
        this._canvas.width = 1024;
        this._canvas.height = 1024;
        this._canvasCtx = this._canvas.getContext('2d');
        // Draw initial placeholder
        this._drawPlaceholder();
        this._texture = new CanvasTexture(this._canvas);
        this._texture.minFilter = LinearFilter;
        this._texture.colorSpace = SRGBColorSpace;
        this._texture.needsUpdate = true;
        const geometry = new PlaneGeometry(2, 2);
        const material = new MeshBasicMaterial({
            map: this._texture,
            side: DoubleSide,
            transparent: false,
        });
        this._mesh = new Mesh(geometry, material);
        this._mesh.name = 'PDFMesh';
        this.add(this._mesh);
    }
    _drawPlaceholder() {
        const ctx = this._canvasCtx;
        const width = this._canvas.width;
        const height = this._canvas.height;
        // Background
        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(0, 0, width, height);
        // Draw PDF icon
        ctx.fillStyle = '#666';
        ctx.font = '48px system-ui';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('📄', width / 2, height / 2);
        // Border
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 2;
        ctx.strokeRect(1, 1, width - 2, height - 2);
    }
    async _loadPDFJS() {
        if (this._pdfjsLib) {
            return this._pdfjsLib;
        }
        // Check if already loaded globally
        if (window.pdfjsLib) {
            this._pdfjsLib = window.pdfjsLib;
            return this._pdfjsLib;
        }
        // Load PDF.js from CDN
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
            script.onload = () => {
                if (window.pdfjsLib) {
                    // Set worker source
                    window.pdfjsLib.GlobalWorkerOptions.workerSrc =
                        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
                    this._pdfjsLib = window.pdfjsLib;
                    resolve(this._pdfjsLib);
                }
                else {
                    reject(new Error('Failed to load PDF.js library'));
                }
            };
            script.onerror = () => reject(new Error('Failed to load PDF.js from CDN'));
            document.head.appendChild(script);
        });
    }
    async _loadPDF(src) {
        const pdfjs = await this._loadPDFJS();
        const loadingTask = pdfjs.getDocument(src);
        return await loadingTask.promise;
    }
    async _renderPage(pageNumber) {
        if (!this._pdf || pageNumber < 1 || pageNumber > this._totalPages) {
            return;
        }
        try {
            const page = await this._pdf.getPage(pageNumber);
            const viewport = page.getViewport({ scale: 2 });
            // Update canvas dimensions
            this._canvas.width = viewport.width;
            this._canvas.height = viewport.height;
            // Clear canvas
            this._canvasCtx.clearRect(0, 0, this._canvas.width, this._canvas.height);
            // Render the page
            await page.render({
                canvasContext: this._canvasCtx,
                viewport: viewport,
            }).promise;
            // Update texture
            this._texture.needsUpdate = true;
            // Update mesh geometry to match aspect ratio
            const ratio = viewport.height / viewport.width;
            const geometry = new PlaneGeometry(2, 2 * ratio);
            this._mesh.geometry.dispose();
            this._mesh.geometry = geometry;
            this._currentPage = pageNumber;
            // Force a few frames of rendering to ensure texture is updated
            await new Promise(resolve => requestAnimationFrame(resolve));
            await new Promise(resolve => requestAnimationFrame(resolve));
        }
        catch (error) {
            console.error(`Failed to render PDF page ${pageNumber}:`, error);
        }
    }
    get src() {
        return this._src;
    }
    set src(src) {
        if (src) {
            this.load(src).catch(console.error);
        }
    }
    get currentPage() {
        return this._currentPage;
    }
    get totalPages() {
        return this._totalPages;
    }
    async load(src) {
        this._src = src;
        try {
            // Load PDF.js library
            await this._loadPDFJS();
            // Load PDF document
            this._pdf = await this._loadPDF(src);
            this._totalPages = this._pdf.numPages;
            this._currentPage = 1;
            // Render first page
            await this._renderPage(1);
            return this;
        }
        catch (error) {
            console.error('Failed to load PDF:', error);
            throw error;
        }
    }
    async goToPage(pageNumber) {
        if (pageNumber >= 1 && pageNumber <= this._totalPages) {
            await this._renderPage(pageNumber);
        }
    }
    async nextPage() {
        if (this._currentPage < this._totalPages) {
            await this.goToPage(this._currentPage + 1);
        }
    }
    async previousPage() {
        if (this._currentPage > 1) {
            await this.goToPage(this._currentPage - 1);
        }
    }
    copy(source, recursive) {
        super.copy(source, recursive);
        this._src = source._src;
        this._currentPage = source._currentPage;
        this._totalPages = source._totalPages;
        return this;
    }
    /**
     * Returns true if the PDF needs continuous rendering.
     * PDFs always need continuous rendering to ensure texture updates are visible.
     */
    needsContinuousRendering() {
        return this._pdf !== null; // Always render continuously if PDF is loaded
    }
    dispose() {
        if (this._texture) {
            this._texture.dispose();
        }
        if (this._mesh) {
            this._mesh.geometry.dispose();
            const material = this._mesh.material;
            if (material.map) {
                material.map.dispose();
            }
            material.dispose();
            this.remove(this._mesh);
        }
    }
}
//# sourceMappingURL=PDFObject.js.map